<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import { usePersonStore } from '@/stores/v2/person'
import { createPerson } from '@/factories/personFactory'
import type { Person } from '@/types/Person'

const props = defineProps<{
  visible: boolean
  person: Person | null
}>()

const emits = defineEmits(['update:visible', 'saved'])

const personStore = usePersonStore()

// Initialize with createPerson factory
const editedPerson = ref<Person>(createPerson())

// computed property to handle visibility
const dialogVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emits('update:visible', value),
})

// Watch for changes to the person prop
watch(
  () => props.person,
  (newVal) => {
    if (newVal) {
      // If we have a person, use it
      editedPerson.value = { ...newVal }
    } else {
      // If person is null, reset to a new person without an ID
      const { id: _id, ...newPerson } = createPerson()
      editedPerson.value = newPerson as Person
    }
  },
  { immediate: true },
)

const cancel = () => {
  emits('update:visible', false)
}

const save = async () => {
  try {
    if (editedPerson.value.id) {
      await personStore.update(editedPerson.value.id, editedPerson.value)
    } else {
      // For new persons, create a new object without the ID
      const { id: _id, ...personWithoutId } = editedPerson.value
      const newId = await personStore.create(personWithoutId)
      editedPerson.value = { ...editedPerson.value, id: newId }
    }
    emits('saved', editedPerson.value)
    emits('update:visible', false)
  } catch (error) {
    console.error('Failed to save person:', error)
  }
}
</script>

<template>
  <Dialog
    v-model:visible="dialogVisible"
    :style="{ width: '450px' }"
    :header="editedPerson.id ? 'Edit Contact' : 'Add Contact'"
    :modal="true"
    class="p-fluid"
  >
    <div class="field">
      <label for="firstName">First Name</label>
      <InputText id="firstName" v-model="editedPerson.firstName" required autofocus />
    </div>
    <div class="field">
      <label for="lastName">Last Name</label>
      <InputText id="lastName" v-model="editedPerson.lastName" required />
    </div>
    <div class="field">
      <label for="jobTitle">Job Title</label>
      <InputText id="jobTitle" v-model="editedPerson.jobTitle" />
    </div>
    <div class="field">
      <label for="contactFor">Contact For</label>
      <InputText id="contactFor" v-model="editedPerson.contactFor" />
    </div>
    <div class="field">
      <label for="email">Email</label>
      <InputText id="email" v-model="editedPerson.email" />
    </div>
    <div class="field">
      <label for="phone">Phone</label>
      <InputText id="phone" v-model="editedPerson.phone" />
    </div>
    <template #footer>
      <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="cancel" />
      <Button label="Save" icon="pi pi-check" class="p-button-text" @click="save" />
    </template>
  </Dialog>
</template>

<style scoped>
.field {
  margin-bottom: 1rem;
}
</style>
